import React, { useState } from "react";
import { CalendarCheck, CheckCircle2, Clock, MapPin, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import Seo from "../seo";
import { api, formatApiError, CLINIC } from "../api";
import { Reveal, Chapter } from "../components/motion";
import { useLanguage } from "../i18n";

const TIME_SLOTS = ["Morning", "Afternoon", "Evening", "No preference"];

export default function Appointment() {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        name: "", phone: "", email: "", preferred_date: "",
        preferred_time: "No preference", reason: "", consent: false, website: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.consent) { setError("Please confirm consent before submitting."); return; }
        setSubmitting(true);
        try {
            await api.post("/enquiries", { ...form, email: form.email || null });
            setDone(true);
            toast.success("Appointment enquiry received.");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = "w-full rounded-lg border border-input bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-accent";

    return (
        <div className="mx-auto max-w-7xl px-5 py-14 lg:px-10 lg:py-20">
            <Seo title="Book an Appointment | Dr. Krishnakanth AVB, Urologist Madurai"
                description="Book a consultation with Dr. Krishnakanth AVB, Consultant Urologist at Preethi Multispeciality Hospital, Madurai. Send an appointment enquiry and the clinic will contact you to confirm." />
            <Chapter index={1} label="Book an Appointment" />
            <Reveal>
                <h1 className="mt-8 font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">{t("book_appointment")}</h1>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-steel md:text-lg">
                    Dr. Krishnakanth AVB — Consultant Urologist, Preethi Multispeciality Hospital, Madurai.
                </p>
            </Reveal>

            <div className="mt-14 grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
                <Reveal className="space-y-8">
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><MapPin className="h-5 w-5 text-accent" /> Clinic location</h2>
                        <p className="mt-3 text-sm leading-relaxed text-navy font-medium">{CLINIC.hospital}</p>
                        <p className="mt-1 text-sm leading-relaxed text-steel" data-testid="clinic-address">{CLINIC.address}</p>
                    </div>
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><Clock className="h-5 w-5 text-accent" /> OPD timings</h2>
                        <ul className="mt-4 overflow-hidden rounded-xl border border-border bg-white" data-testid="opd-timings">
                            {CLINIC.opdSchedule.map((d) => (
                                <li key={d.day} className="flex items-center justify-between border-b border-border px-5 py-2.5 text-sm last:border-b-0">
                                    <span className="font-medium text-navy">{d.day}</span>
                                    <span className={d.hours.includes("Closed") ? "text-slate-400" : "text-steel"}>{d.hours}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700" data-testid="emergency-note">
                            <Phone className="h-4 w-4" aria-hidden="true" />
                            {CLINIC.emergency} — call {CLINIC.phoneDisplay}
                        </p>
                    </div>
                    <div>
                        <h2 className="flex items-center gap-2 font-display text-xl font-medium text-navy"><Phone className="h-5 w-5 text-accent" /> Appointments by phone</h2>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href={CLINIC.phoneHref} data-testid="appointment-call-btn"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-all duration-300 hover:border-accent hover:text-accent">
                                <Phone className="h-4 w-4" aria-hidden="true" />
                                Call {CLINIC.phoneDisplay}
                            </a>
                            <a href={CLINIC.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="appointment-whatsapp-btn"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-all duration-300 hover:border-accent hover:text-accent">
                                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                                WhatsApp
                            </a>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    {done ? (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="appointment-success-message">
                            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" aria-hidden="true" />
                            <h2 className="mt-5 font-display text-2xl font-medium text-navy">Thank you.</h2>
                            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-steel">
                                Your appointment enquiry has been received. The clinic will contact you on the phone
                                number provided to confirm your appointment.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="rounded-2xl border border-border bg-mist p-7 sm:p-9" data-testid="appointment-form">
                            <h2 className="font-display text-xl font-medium text-navy">Appointment enquiry</h2>
                            <p className="mt-2 text-xs leading-relaxed text-steel">
                                Please do not enter highly sensitive medical information in this form. A brief reason
                                (e.g. "stone pain", "urinary symptoms") is sufficient.
                            </p>
                            <div className="mt-7 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="appt-name" className="mb-1.5 block text-xs font-semibold text-navy">Full name *</label>
                                    <input id="appt-name" data-testid="appointment-name-input" required minLength={2} maxLength={100}
                                        value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                                </div>
                                <div>
                                    <label htmlFor="appt-phone" className="mb-1.5 block text-xs font-semibold text-navy">Phone number *</label>
                                    <input id="appt-phone" data-testid="appointment-phone-input" required type="tel" maxLength={20}
                                        value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 …" />
                                </div>
                                <div>
                                    <label htmlFor="appt-email" className="mb-1.5 block text-xs font-semibold text-navy">Email (optional)</label>
                                    <input id="appt-email" data-testid="appointment-email-input" type="email"
                                        value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
                                </div>
                                <div>
                                    <label htmlFor="appt-date" className="mb-1.5 block text-xs font-semibold text-navy">Preferred date</label>
                                    <input id="appt-date" data-testid="appointment-date-input" type="date"
                                        value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="appt-time" className="mb-1.5 block text-xs font-semibold text-navy">Preferred time</label>
                                    <select id="appt-time" data-testid="appointment-time-select" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls}>
                                        {TIME_SLOTS.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="appt-reason" className="mb-1.5 block text-xs font-semibold text-navy">Reason for consultation (optional)</label>
                                    <textarea id="appt-reason" data-testid="appointment-reason-input" rows={3} maxLength={1000}
                                        value={form.reason} onChange={set("reason")} className={inputCls}
                                        placeholder="A short description is enough — please avoid detailed medical history here." />
                                </div>
                                <input type="text" name="website" value={form.website} onChange={set("website")}
                                    tabIndex={-1} autoComplete="off" aria-hidden="true"
                                    style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} />
                                <div className="sm:col-span-2">
                                    <label className="flex items-start gap-3 text-xs leading-relaxed text-steel">
                                        <input type="checkbox" data-testid="appointment-consent-checkbox" checked={form.consent} onChange={set("consent")}
                                            className="mt-0.5 h-4 w-4 rounded border-input accent-[#0A192F]" />
                                        I consent to being contacted by the clinic regarding this appointment request, and to the
                                        information provided being used for that purpose as described in the Privacy Policy. I understand
                                        this form is not for emergencies or for sending detailed medical records.
                                    </label>
                                </div>
                            </div>
                            {error && <p className="mt-4 text-sm font-medium text-red-600" data-testid="appointment-error-message">{error}</p>}
                            <button type="submit" disabled={submitting} data-testid="appointment-submit-btn"
                                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent disabled:opacity-60 sm:w-auto">
                                <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                                {submitting ? "Sending…" : "Send Appointment Enquiry"}
                            </button>
                        </form>
                    )}
                </Reveal>
            </div>
        </div>
    );
}
